/**
 * Business Service
 * Handles API communication for business CRUD operations
 */

import axios from 'axios';

const getApiBaseUrl = () => {
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
    return '/api';
  }

  return process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
};

const API_BASE_URL = getApiBaseUrl();

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
});

/**
 * Get all businesses
 */
export const getAllBusinesses = async () => {
  try {
    const response = await api.get('/businesses');
    return response.data.businesses || [];
  } catch (error) {
    console.error('Error fetching businesses:', error);
    throw error;
  }
};

/**
 * Get businesses by category
 */
export const getBusinessesByCategory = async (category) => {
  try {
    const response = await api.get('/businesses');
    const allBusinesses = response.data.businesses || [];
    return allBusinesses.filter(b => b.category === category && b.approved);
  } catch (error) {
    console.error('Error fetching businesses by category:', error);
    throw error;
  }
};

/**
 * Get approved businesses only
 */
export const getApprovedBusinesses = async () => {
  try {
    const response = await api.get('/businesses');
    const allBusinesses = response.data.businesses || [];
    return allBusinesses.filter(b => b.approved);
  } catch (error) {
    console.error('Error fetching approved businesses:', error);
    throw error;
  }
};

/**
 * Get pending businesses (not approved)
 */
export const getPendingBusinesses = async () => {
  try {
    const response = await api.get('/businesses');
    const allBusinesses = response.data.businesses || [];
    return allBusinesses.filter(b => !b.approved);
  } catch (error) {
    console.error('Error fetching pending businesses:', error);
    throw error;
  }
};

/**
 * Add new business
 */
export const addBusiness = async (businessData) => {
  try {
    const response = await api.post('/businesses', {
      name: businessData.name,
      category: businessData.category,
      type: businessData.type,
      address: businessData.address,
      phone: businessData.phone,
      hours: businessData.hours,
      email: businessData.email || '',
      website: businessData.website || '',
      description: businessData.description || '',
      latitude: businessData.latitude || '',
      longitude: businessData.longitude || '',
      imageUrls: businessData.photos || []
    });

    return response.data.business;
  } catch (error) {
    console.error('Error adding business:', error);
    throw error;
  }
};

/**
 * Update business
 */
export const updateBusiness = async (businessId, updatedData) => {
  try {
    const response = await api.post(`/businesses/${businessId}/update`, updatedData);
    return response.data.business;
  } catch (error) {
    console.error('Error updating business:', error);
    throw error;
  }
};

/**
 * Delete business
 */
export const deleteBusiness = async (businessId) => {
  try {
    const response = await api.delete(`/businesses/${businessId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting business:', error);
    throw error;
  }
};

/**
 * Approve business
 */
export const approveBusiness = async (businessId) => {
  try {
    const response = await api.post(`/approve/${businessId}`);
    return response.data;
  } catch (error) {
    console.error('Error approving business:', error);
    throw error;
  }
};

/**
 * Search businesses
 */
export const searchBusinesses = async (searchTerm) => {
  try {
    const response = await api.get('/businesses');
    const allBusinesses = response.data.businesses || [];
    
    return allBusinesses.filter(b =>
      (b.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
       b.address.toLowerCase().includes(searchTerm.toLowerCase())) &&
      b.approved
    );
  } catch (error) {
    console.error('Error searching businesses:', error);
    throw error;
  }
};

/**
 * Get business statistics
 */
export const getStatistics = async () => {
  try {
    const response = await api.get('/stats');
    return response.data;
  } catch (error) {
    console.error('Error fetching statistics:', error);
    throw error;
  }
};

/**
 * Export businesses as JSON
 */
export const exportBusinessesAsJSON = async () => {
  try {
    const response = await api.get('/businesses');
    const data = {
      businesses: response.data.businesses || [],
      exportedAt: new Date().toISOString(),
      totalCount: (response.data.businesses || []).length
    };

    // Create download link
    const dataStr = JSON.stringify(data, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    const exportFileDefaultName = `sangli-businesses-${new Date().toISOString().split('T')[0]}.json`;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();

    return data;
  } catch (error) {
    console.error('Error exporting businesses:', error);
    throw error;
  }
};

/**
 * Handle API errors
 */
export const handleApiError = (error) => {
  if (error.response) {
    // Server responded with error status
    console.error('API Error:', error.response.data);
    return error.response.data.error || 'An error occurred';
  } else if (error.request) {
    // Request made but no response
    console.error('No response from server:', error.request);
    return 'No response from server. Make sure backend is running on port 5000.';
  } else {
    // Other errors
    console.error('Error:', error.message);
    return error.message || 'An unknown error occurred';
  }
};

const businessServiceApi = {
  getAllBusinesses,
  getBusinessesByCategory,
  getApprovedBusinesses,
  getPendingBusinesses,
  addBusiness,
  updateBusiness,
  deleteBusiness,
  approveBusiness,
  searchBusinesses,
  getStatistics,
  exportBusinessesAsJSON,
  handleApiError
};

export default businessServiceApi;
